'use strict';

define('vb/private/debug/actionChainDebugStream',['vb/private/debug/debugStream', 'vb/private/utils', 'vb/private/debug/constants',
  'vb/private/constants'],
(DebugStream, Utils, DebugConstants, Constants) => {
  /**
   * Debug stream for an action chain.
   */
  class ActionChainDebugStream extends DebugStream {
    constructor(chainId, executionContext) {
      super(DebugConstants.DebuggeeType.ACTION_CHAIN);

      this.chainId = chainId;
      this.executionContext = executionContext;

      // the accumulated content of the stream
      this.content = {
        type: 'actionChainExecution',
        chainId,
        execId: this.id,
        $chain: {
          results: {},
          actions: [],
        },
      };

      this.actionFrequency = {};
    }

    /**
     * Action chain debug streams are only enabled for the action chain tester recorder.
     *
     * @returns {boolean}
     */
    get isEnabled() {
      return this.isTestMode;
    }

    /**
     * Clone the current variable state in $application, $package, $flow, $page and $chain.
     */
    cloneState() {
      const scopes = this.getScopes();
      const state = ActionChainDebugStream.cloneScopes(scopes);

      const { $base } = scopes;
      if ($base) {
        state.$base = ActionChainDebugStream.cloneScopes($base, true);
      }

      return state;
    }

    getScopes() {
      return this.executionContext[Constants.CHAIN_INTERNAL_CONTEXT].availableContexts || this.executionContext;
    }

    static cloneScopes(scopes, strip$) {
      const state = {};

      ['$chain', '$fragment', '$layout', '$page', '$flow', '$application', '$global'].forEach((name) => {
        const scopeName = strip$ ? name.substring(1) : name;
        const scope = scopes[scopeName];

        if (scope) {
          state[scopeName] = {
            variables: DebugStream.cloneObject(scope.variables),
            constants: DebugStream.cloneObject(scope.constants),
          };
        }
      });

      return state;
    }

    /**
     * Log the starting state of the action chain.
     */
    start(parameters) {
      if (this.isEnabled) {
        // log the parameters to the chain
        this.content.$chain.parameters = Utils.cloneObject(parameters);

        // log the context before the chain is executed
        this.content.$chain.beforeContext = this.cloneState();
      }

      return Promise.resolve();
    }

    /**
     * Log the starting state of the given action.
     *
     * @param action the action to log
     * @param parameters the parameters for the action
     */
    actionStart(action, parameters) {
      if (this.isEnabled) {
        const actionState = {
          id: action.id,
          parameters: DebugStream.cloneObject(parameters),
          beforeContext: this.cloneState(),
        };

        const actionsLength = this.content.$chain.actions.length;
        const actionUniqueId = ActionChainDebugStream.stripUniqueId(action.id);

        // To avoid out-of-memory crashes in case of infinite loops
        if (actionsLength > DebugConstants.MAX_ACTIONS_LENGTH) {
          // Remove only context of actions that repeats
          // and preserve the context of actions that are not part of infinite loops
          ActionChainDebugStream.removeActionContext(this.content.$chain.actions, this.actionFrequency);
          console.warn('Potential infinite loop detected');
        }

        this.content.$chain.actions.push(actionState);
        this.actionFrequency[actionUniqueId] = this.actionFrequency[actionUniqueId]
          ? this.actionFrequency[actionUniqueId] + 1 : 1;
        actionState.startTime = new Date().getTime();
      }

      return Promise.resolve();
    }

    /**
     * Log the ending state of the given action.
     *
     * @param action the action to log
     * @param outcome the outcome of the action
     */
    actionEnd(action, outcome) {
      if (this.isEnabled) {
        const endTime = new Date().getTime();
        const actionState = this.content.$chain.actions.find((element) => element.id === action.id);

        if (actionState) {
          actionState.outcome = DebugStream.cloneObject(outcome);
          actionState.afterContext = this.cloneState();
          actionState.completionTime = `${endTime - actionState.startTime} ms`;
        }

        const id = ActionChainDebugStream.stripUniqueId(action.id);
        this.content.$chain.results[id] = outcome.result;
      }
    }

    /**
     * Log error thrown from the action chain.
     *
     * @param error error to log
     */
    logError(error) {
      this.content.$chain.error = error;
    }

    /**
     * Log the ending state plus return value of the action chain.
     *
     * @param returnValue returnValue of the action chain
     */
    end(returnValue) {
      if (this.isEnabled) {
        this.content.$chain.return = returnValue;
        this.content.$chain.afterContext = this.content.$chain.afterContext || this.cloneState();
      }
    }

    /**
    * Removes repeated action contexts from the provided array to prevent memory overflow.
    *
    * This method iterates through the array of action contexts and removes any actions that appear more than once,
    * as long as the total number of removals does not exceed
    * the maximum allowed count defined by `MEMORY_PROTECTION_REMOVECOUNT`.
    *
    * @param {Array<Object>} actionsArr - The array of action contexts to process.
    * @param {Object<string, number>} actionFrequency - A lookup table mapping each action's unique ID to its frequency.
    * @returns {void}
    */
    static removeActionContext(actionsArr, actionFrequency) {
      let removedCount = 0;
      const frequencies = actionFrequency;

      for (let i = 0; i < actionsArr.length; i++) {
        const action = actionsArr[i];
        const actionUniqueId = ActionChainDebugStream.stripUniqueId(action.id);
        if (frequencies[actionUniqueId] > 1 && removedCount < DebugConstants.MEMORY_PROTECTION_REMOVECOUNT) {
          actionsArr.splice(i, 1);
          removedCount += 1;
          frequencies[actionUniqueId] -= 1;
        }
      }
    }

    /**
    * Strip off unique id
    * @param {String} actionId The action ID, which includes a unique suffix.
    * @returns {string} The action ID with the unique suffix removed.
    */
    static stripUniqueId(actionId) {
      const index = actionId.lastIndexOf('_');
      if (index !== -1) {
        return actionId.substring(0, index);
      }
      return actionId;
    }
  }

  return ActionChainDebugStream;
});

